import * as React from "react";

import { Box, makeStyles, Theme, Typography } from "@material-ui/core";

import CashAppIcon1 from "client/assets/login-value-props-icon1-cash.svg";
import AfterpayIcon1 from "client/assets/login-value-props-icon1.svg";
import CashAppIcon2 from "client/assets/login-value-props-icon2-cash.svg";
import AfterpayIcon2 from "client/assets/login-value-props-icon2.svg";
import CashAppIcon3 from "client/assets/login-value-props-icon3-cash.svg";
import AfterpayIcon3 from "client/assets/login-value-props-icon3.svg";
import { useBranding } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";

const useStyles = makeStyles((theme: Theme) => {
  const { black, white } = theme.palette.common;
  const { secondValuePropOffset } = useNormalizedBrandPaletteValues(theme);

  return {
    factBody: {
      color: white,
      lineHeight: 1.5,
    },
    factHeader: {
      fontWeight: theme.typography.fontWeightBold,
      color: white,
      marginBottom: theme.spacing(1),
    },
    dataFactWrapper: {
      display: "flex",
      [theme.breakpoints.up("md")]: {
        maxWidth: "50%",
        width: "50%",
      },
      [theme.breakpoints.down("sm")]: {
        margin: `0 ${theme.spacing(3)}px ${theme.spacing(3)}px `,
      },
    },
    dataFactsWrapper: {
      backgroundColor: black,
      height: "100%",
      padding: `${theme.spacing(3)}px 0`,
      [theme.breakpoints.down("sm")]: {
        alignItems: "flex-start",
      },
    },
    secondValuePropIcon: {
      // we do this one off because the SVG exported has spacing included, so we make up for it by pushing it against the extra space
      transform: secondValuePropOffset,
    },
  };
});

export const ValueProp: React.FC<{
  icon: React.ReactNode;
  body: React.ReactNode;
  header: string;
}> = ({ header, body, icon }) => {
  const classes = useStyles();
  const { brandKey } = useBranding();
  const typographyVariant = brandKey === "CashApp" ? "body1" : "body2";

  return (
    <Box className={classes.dataFactWrapper} mb={10}>
      {icon}
      <Box>
        <Typography variant={typographyVariant} className={classes.factHeader}>
          {header}
        </Typography>
        <Typography className={classes.factBody} variant={typographyVariant}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

export const ValueProps: React.FC<{
  variant?: "login" | "lead";
  isMobileView?: boolean;
}> = ({ variant = "lead", isMobileView = false }) => {
  const classes = useStyles();
  const { brandTitle, brandKey } = useBranding();

  const isCashApp = brandKey === "CashApp";
  const valuePropIcon1 = isCashApp ? CashAppIcon1 : AfterpayIcon1;
  const valuePropIcon2 = isCashApp ? CashAppIcon2 : AfterpayIcon2;
  const valuePropIcon3 = isCashApp ? CashAppIcon3 : AfterpayIcon3;

  const defaultValueProps = [
    {
      icon: (
        <Box mr={3}>
          <img src={valuePropIcon1} alt="icon1" />
        </Box>
      ),
      header: "More customers",
      body: (
        <>
          Our users try new places. <strong>30% or more</strong> of a store’s{" "}
          {brandTitle} shoppers are first-time customers.
        </>
      ),
    },
    {
      icon: (
        <Box mr={isCashApp ? 3 : undefined}>
          <img
            className={classes.secondValuePropIcon}
            src={valuePropIcon2}
            alt="icon2"
          />
        </Box>
      ),
      header: "Higher conversion",
      body: (
        <>
          Our technology converts. {brandTitle} can grow cart conversions by{" "}
          <strong>up to 22%</strong>, plus more repeat shoppers.
        </>
      ),
    },
    {
      icon: (
        <Box mr={3}>
          <img src={valuePropIcon3} alt="icon3" />
        </Box>
      ),
      header: "Join the afterparty",
      body: (
        <>
          Our community spends more. Stores that join {brandTitle} can boost
          their average order values by <strong>up to 40%</strong>.
        </>
      ),
    },
  ];

  return (
    <Box
      alignItems="center"
      className={classes.dataFactsWrapper}
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      {defaultValueProps.map((valueProp, index) => {
        // This component is shared between lead and login form. if variant is login and were on mobile, we only want to show the first value prop and exclude the rest
        if (variant === "login" && isMobileView) {
          if (index === 0) {
            return (
              <ValueProp
                key={valueProp.header}
                header={valueProp.header}
                body={valueProp.body}
                icon={valueProp.icon}
              />
            );
          }
          return null;
        }
        return (
          <ValueProp
            key={valueProp.header}
            header={valueProp.header}
            body={valueProp.body}
            icon={valueProp.icon}
          />
        );
      })}
    </Box>
  );
};
